if (!customElements.get('ts-customization-studio')) {
  class TsCustomizationStudio extends HTMLElement {
    connectedCallback() {
      this.modal = this.querySelector('.ts-studio');
      this.form = this.querySelector('.ts-studio__form');
      this.garment = this.querySelector('.ts-studio__garment');
      this.designImage = this.querySelector('.ts-studio__design-image');
      this.designText = this.querySelector('.ts-studio__design-text');
      this.placeholder = this.querySelector('.ts-studio__placeholder');
      this.variantSelect = this.querySelector('[data-variant-select]');
      this.fileInput = this.querySelector('[data-design-file]');
      this.textInput = this.querySelector('[data-design-text]');
      this.scaleInput = this.querySelector('[data-design-scale]');
      this.fontSelect = this.querySelector('[data-text-font]');
      this.colorInput = this.querySelector('[data-text-color]');
      this.sizeInput = this.querySelector('[data-text-size]');
      this.quantityInput = this.querySelector('[name="quantity"]');
      this.stepIndex = 0;
      this.product = null;
      this.objectUrl = null;

      this.addEventListener('click', this.onClick.bind(this));
      this.addEventListener('input', this.onInput.bind(this));
      this.addEventListener('change', this.onChange.bind(this));
      this.addEventListener('keydown', this.onKeydown.bind(this));
      this.form.addEventListener('submit', this.onSubmit.bind(this));
    }

    onClick(event) {
      const openButton = event.target.closest('[data-open-studio]');
      if (openButton) {
        this.open(openButton.dataset.productId);
        return;
      }

      if (event.target.closest('[data-close-studio]') || event.target === this.modal) {
        this.close();
        return;
      }

      const next = event.target.closest('[data-studio-next]');
      const previous = event.target.closest('[data-studio-previous]');
      if (next) this.setStep(Math.min(2, this.stepIndex + 1));
      if (previous) this.setStep(Math.max(0, this.stepIndex - 1));
    }

    onInput(event) {
      if (event.target === this.textInput) {
        this.designText.textContent = event.target.value;
        this.syncPlaceholder();
      }
      if (event.target === this.scaleInput) {
        this.style.setProperty('--ts-design-scale', event.target.value);
      }
      if (event.target === this.colorInput) {
        this.style.setProperty('--ts-text-color', event.target.value);
      }
      if (event.target === this.sizeInput) {
        this.style.setProperty('--ts-text-size', `${event.target.value}px`);
      }
      this.updateSummary();
    }

    onChange(event) {
      if (event.target === this.fileInput) this.previewFile(event.target.files[0]);
      if (event.target === this.fontSelect) {
        this.style.setProperty('--ts-text-font', event.target.value);
      }
      if (event.target === this.variantSelect) this.updateVariant();
      this.updateSummary();
    }

    onKeydown(event) {
      if (event.key === 'Escape' && this.modal.hasAttribute('open')) this.close();
    }

    open(productId) {
      const script = this.querySelector(`script[data-ts-product="${CSS.escape(productId)}"]`);
      if (!script) return;

      this.product = JSON.parse(script.textContent);
      this.querySelector('[data-studio-title]').textContent = this.product.title;
      this.garment.src = this.product.featured_image || '';
      this.populateVariants();
      this.resetDesign();
      this.setStep(0);
      this.modal.setAttribute('open', '');
      this.modal.setAttribute('aria-hidden', 'false');
      document.body.classList.add('ts-studio-open');
      this.querySelector('[data-close-studio]').focus();
    }

    close() {
      this.modal.removeAttribute('open');
      this.modal.setAttribute('aria-hidden', 'true');
      document.body.classList.remove('ts-studio-open');
    }

    populateVariants() {
      this.variantSelect.innerHTML = '';
      this.product.variants.forEach((variant) => {
        const option = document.createElement('option');
        option.value = variant.id;
        option.textContent = `${variant.title} — ${this.formatMoney(variant.price)}${variant.available ? '' : ' (Sold out)'}`;
        option.disabled = !variant.available;
        if (variant.available && !this.variantSelect.value) option.selected = true;
        this.variantSelect.appendChild(option);
      });
      this.updateVariant();
    }

    updateVariant() {
      if (!this.product) return;
      const variant = this.product.variants.find((item) => String(item.id) === this.variantSelect.value);
      if (!variant) return;
      this.form.querySelector('[name="id"]').value = variant.id;
      const variantImage = variant.featured_image && variant.featured_image.src;
      if (variantImage) this.garment.src = variantImage;
      this.querySelector('[data-studio-price]').textContent = this.formatMoney(variant.price);
      this.querySelector('[data-add-customized]').disabled = !variant.available;
      this.updateSummary();
    }

    previewFile(file) {
      const error = this.querySelector('[data-file-error]');
      error.textContent = '';
      if (!file) {
        this.designImage.style.display = 'none';
        this.syncPlaceholder();
        return;
      }
      if (!file.type.startsWith('image/')) {
        error.textContent = this.dataset.invalidFileMessage;
        this.fileInput.value = '';
        return;
      }
      const maxBytes = Number(this.dataset.maxFileMb || 10) * 1024 * 1024;
      if (file.size > maxBytes) {
        error.textContent = this.dataset.largeFileMessage;
        this.fileInput.value = '';
        return;
      }
      if (this.objectUrl) URL.revokeObjectURL(this.objectUrl);
      this.objectUrl = URL.createObjectURL(file);
      this.designImage.src = this.objectUrl;
      this.designImage.style.display = 'block';
      this.syncPlaceholder();
    }

    resetDesign() {
      this.form.reset();
      this.designImage.removeAttribute('src');
      this.designImage.style.display = 'none';
      this.designText.textContent = '';
      this.style.setProperty('--ts-design-scale', '1');
      this.style.setProperty('--ts-text-color', this.colorInput.value || '#111111');
      this.style.setProperty('--ts-text-size', `${this.sizeInput.value || 24}px`);
      this.syncPlaceholder();
    }

    syncPlaceholder() {
      const hasImage = this.fileInput.files && this.fileInput.files.length;
      const hasText = this.textInput.value.trim().length;
      this.placeholder.hidden = Boolean(hasImage || hasText);
    }

    setStep(index) {
      this.stepIndex = index;
      this.querySelectorAll('[data-studio-step]').forEach((step, stepIndex) => {
        step.classList.toggle('is-active', stepIndex === index);
      });
      this.querySelectorAll('[data-progress-step]').forEach((item, stepIndex) => {
        item.classList.toggle('is-active', stepIndex <= index);
      });
      this.updateSummary();
    }

    updateSummary() {
      if (!this.product) return;
      const selected = this.variantSelect.options[this.variantSelect.selectedIndex];
      this.querySelector('[data-summary-product]').textContent = this.product.title;
      this.querySelector('[data-summary-variant]').textContent = selected ? selected.textContent : '';
      this.querySelector('[data-summary-design]').textContent =
        [
          this.fileInput.files && this.fileInput.files[0] ? this.fileInput.files[0].name : '',
          this.textInput.value.trim()
        ].filter(Boolean).join(' + ') || this.dataset.noDesignMessage;

      this.form.querySelector('[name="properties[Custom text]"]').value = this.textInput.value.trim();
      this.form.querySelector('[name="properties[Text color]"]').value = this.colorInput.value;
      this.form.querySelector('[name="properties[Text font]"]').value = this.fontSelect.value;
      this.form.querySelector('[name="properties[Design scale]"]').value = `${Math.round(Number(this.scaleInput.value) * 100)}%`;
    }

    onSubmit(event) {
      const hasImage = this.fileInput.files && this.fileInput.files.length;
      const hasText = this.textInput.value.trim().length;
      const error = this.querySelector('[data-design-error]');
      if (this.dataset.requireDesign === 'true' && !hasImage && !hasText) {
        event.preventDefault();
        error.textContent = this.dataset.requiredMessage;
        this.setStep(1);
        return;
      }
      error.textContent = '';
      this.querySelector('[data-add-customized]').textContent = this.dataset.addingLabel;
      this.querySelector('[data-add-customized]').disabled = true;
    }

    formatMoney(cents) {
      const amount = Number(cents || 0) / 100;
      try {
        return new Intl.NumberFormat(document.documentElement.lang || 'en-IN', {
          style: 'currency',
          currency: this.dataset.currency || 'INR',
          maximumFractionDigits: 2
        }).format(amount);
      } catch (error) {
        return `${this.dataset.currency || 'INR'} ${amount.toFixed(2)}`;
      }
    }
  }

  customElements.define('ts-customization-studio', TsCustomizationStudio);
}
